import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../services/project.service.js';
import './ProjectGrid.css';

const Modal = ({ isOpen, onClose, project, onLanguageSelect }) => {
    if (!isOpen || !project) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="language_select_modal" onClick={handleBackdropClick}>
            <div className="language_select_content">
                <div className="language_select_header">
                    <h2 className="language_select_title">Éditer le projet</h2>
                    <button className="language_select_close" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="language_select_description">
                    <div className="language_select_info">
                        <i className="fas fa-info-circle"></i>
                        <p>Sélectionnez la langue dans laquelle vous souhaitez éditer ce projet</p>
                    </div>
                </div>

                <div className="language_select_grid">
                    {project.projects.map(project => (
                        <div
                            key={project.id}
                            className="language_select_option"
                            onClick={() => onLanguageSelect(project.id)}
                        >
                            <div className="language_select_icon">
                                <i className="fas fa-language"></i>
                            </div>
                            <div className="language_select_details">
                                <span className="language_select_name">{project.language_name}</span>
                                <span className="language_select_label">Cliquez pour éditer</span>
                            </div>
                            <i className="fas fa-chevron-right language_select_arrow"></i>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Ajouter le nouveau composant Modal de téléchargement

// Version améliorée du DownloadModal avec options audio/vidéo et style cohérent avec NewProjectModalV2
const DownloadModal = ({ isOpen, onClose, project }) => {
    const [exporting, setExporting] = useState(null);
    const [videoAvailability, setVideoAvailability] = useState({});
    const [checkingVideo, setCheckingVideo] = useState({});

    // Vérifier la disponibilité des vidéos pour chaque projet lorsque le modal s'ouvre
    useEffect(() => {
        if (isOpen && project && project.projects) {
            checkVideoAvailability();
        }
    }, [isOpen, project]);

    const checkVideoAvailability = async () => {
        if (!project?.projects) return;

        // Initialiser l'état de vérification pour chaque projet
        const initialChecking = {};
        project.projects.forEach(proj => {
            initialChecking[proj.id] = true;
        });
        setCheckingVideo(initialChecking);

        // Vérifier chaque projet
        const availability = {};
        for (const proj of project.projects) {
            try {
                const result = await projectService.checkVideoAvailability(proj.id);
                availability[proj.id] = result.available;
            } catch (error) {
                console.error(`Erreur lors de la vérification de la vidéo pour ${proj.id}:`, error);
                availability[proj.id] = false;
            } finally {
                // Marquer ce projet spécifique comme vérifié
                setCheckingVideo(prev => ({
                    ...prev,
                    [proj.id]: false
                }));
            }
        }

        setVideoAvailability(availability);
    };

    const handleExport = async (projectId, languageCode, exportType) => {
        // Marquer comme en cours d'exportation
        setExporting({ projectId, type: exportType });

        try {
            const blob = await projectService.exportAudio(projectId, exportType);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            // File extension based on export type
            const extension = exportType === 'video' ? 'mp4' : 'wav';
            a.download = `project_${projectId}_${languageCode || 'export'}.${extension}`;

            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error(`Erreur lors du téléchargement ${exportType}:`, error);
        } finally {
            setExporting(null);
        }
    };

    if (!isOpen || !project) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="npmodal__language_select_modal" onClick={handleBackdropClick}>
            <div className="npmodal__language_select_content">
                <div className="npmodal__language_select_header">
                    <h2 className="npmodal__language_select_title">Télécharger le projet</h2>
                    <button className="npmodal__language_select_close" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="npmodal__language_select_description">
                    <div className="npmodal__language_select_info">
                        <i className="fas fa-info-circle"></i>
                        <p>Sélectionnez la langue et le format que vous souhaitez télécharger</p>
                    </div>
                </div>

                <div className="npmodal__language_select_grid">
                    {project.projects.map(project => {
                        // Determine if we're checking video availability for this project
                        const isChecking = checkingVideo[project.id] === true;
                        // Determine if video is available for this project
                        const hasVideo = videoAvailability[project.id] === true;

                        return (
                            <div key={project.id} className="npmodal__language_select_option">
                                <div className="npmodal__language_select_option_content">
                                    <div className="npmodal__language_select_icon">
                                        <i className="fas fa-language"></i>
                                    </div>
                                    <div className="npmodal__language_select_details">
                                        <span className="npmodal__language_select_name">{project.language_name || "Langue inconnue"}</span>

                                        <div className="npmodal__language_download_actions">
                                            {/* Bouton Audio */}
                                            <button
                                                className="npmodal__download_btn npmodal__download_btn_audio"
                                                onClick={() => handleExport(project.id, project.language_code, 'audio')}
                                                disabled={exporting !== null}
                                            >
                                                {exporting?.projectId === project.id && exporting?.type === 'audio' ? (
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                ) : (
                                                    <i className="fas fa-music"></i>
                                                )}
                                                <span>
                                                    {exporting?.projectId === project.id && exporting?.type === 'audio'
                                                        ? "Téléchargement..."
                                                        : "Audio"}
                                                </span>
                                            </button>

                                            {/* Bouton Vidéo */}
                                            <button
                                                className="npmodal__download_btn npmodal__download_btn_video"
                                                onClick={() => handleExport(project.id, project.language_code, 'video')}
                                                disabled={exporting !== null || isChecking || !hasVideo}
                                            >
                                                {exporting?.projectId === project.id && exporting?.type === 'video' ? (
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                ) : isChecking ? (
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                ) : !hasVideo ? (
                                                    <i className="fas fa-ban"></i>
                                                ) : (
                                                    <i className="fas fa-film"></i>
                                                )}
                                                <span>
                                                    {exporting?.projectId === project.id && exporting?.type === 'video'
                                                        ? "Téléchargement..."
                                                        : isChecking
                                                            ? "Vérification..."
                                                            : !hasVideo
                                                                ? "Vidéo non disponible"
                                                                : "Vidéo"}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};


const ProjectCard = ({ group, onDelete, onEdit, onDownload }) => {

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'completed':
                return 'Terminé';
            case 'failed':
                return 'Échoué';
            default:
                return 'En traitement';
        }
    };

    const isActionable = group.status === 'completed' || group.status === 'failed';

    const handleDelete = (e) => {
        e.stopPropagation();
        if (isActionable) {
            onDelete();
        }
    };

    return (
        <div className="project-card">
            <div className="project-header">
                <div className="project-info">
                    <h3>{group.project_name}</h3>
                </div>
                <span className={`project-status status-${group.status || 'progress'}`}>
                    {getStatusText(group.status)}
                </span>
            </div>
            <div className="project-meta">
                <i className="far fa-calendar"></i>
                Créé le {formatDate(group.created_at)}
            </div>
            <div className="project-languages">
                {group.projects.map(project => (
                    <span key={project.id} className="language-tag">
                        {project.language_name}
                    </span>
                ))}
            </div>
            <div className="card-actions">
                <button
                    className="action-btn btn-primary"
                    onClick={onEdit}
                    disabled={!isActionable}
                    title={!isActionable ? "L'édition n'est pas disponible pendant le traitement" : "Éditer le projet"}
                >
                    <i className="fas fa-edit"></i>
                    Éditer
                </button>
                <button
                    className="action-btn btn-primary"
                    onClick={onDownload}
                    disabled={!isActionable}
                    title={!isActionable ? "Le téléchargement n'est pas disponible pendant le traitement" : "Télécharger l'audio"}
                >
                    <i className="fas fa-download"></i>
                    Télécharger
                </button>
                <button
                    className="action-btn btn-secondary"
                    onClick={handleDelete}
                    disabled={!isActionable}
                    title={!isActionable ? "La suppression n'est pas disponible pendant le traitement" : "Supprimer le projet"}
                >
                    <i className="fas fa-trash"></i>
                </button>
            </div>
        </div>
    );
};

const ProjectGrid = ({ shouldRefresh, onRefreshComplete, filters }) => {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [filteredGroups, setFilteredGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [selectedDownloadProject, setSelectedDownloadProject] = useState(null);

    const handleDownload = (project) => {
        setSelectedDownloadProject(project);
        setDownloadModalOpen(true);
    };

    const loadGroups = async () => {
        try {
            console.log("ProjectGrid: Début du chargement des données");
            setLoading(true);
            const data = await projectService.getAllProjects();
            console.log("ProjectGrid: Données reçues:", data.length, "projets");
            setGroups(data);
            setFilteredGroups(data);
            setError(null);
        } catch (err) {
            console.error('ProjectGrid: Erreur lors du chargement des projets:', err);
            setError('Impossible de charger les projets');
        } finally {
            setLoading(false);
            console.log("ProjectGrid: Chargement terminé, appel de onRefreshComplete");
            if (typeof onRefreshComplete === 'function') {
                onRefreshComplete();
            }
        }
    };

    useEffect(() => {
        console.log("ProjectGrid: Chargement initial des projets");
        loadGroups();
    }, []);

    // Effet pour surveiller shouldRefresh
    useEffect(() => {
        if (shouldRefresh) {
            console.log("ProjectGrid: shouldRefresh détecté, chargement des projets en cours...");
            loadGroups();
        }
    }, [shouldRefresh]);

    // Mise à jour de la logique de filtrage
    useEffect(() => {
        let result = [...groups];

        // Filtre par recherche
        if (filters?.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter(group =>
                group.project_name.toLowerCase().includes(searchLower)
            );
        }

        // Filtre par statuts
        if (filters?.statuses && filters.statuses.length > 0) {
            result = result.filter(group =>
                filters.statuses.includes(group.status)
            );
        }

        // Filtre par langages
        if (filters?.languages && filters.languages.length > 0) {
            result = result.filter(group =>
                group.projects.some(project =>
                    filters.languages.includes(project.language_code)
                )
            );
        }

        // Filtre par date
        if (filters?.dateRange) {
            if (filters.dateRange.start) {
                const startDate = new Date(filters.dateRange.start);
                result = result.filter(group =>
                    new Date(group.created_at) >= startDate
                );
            }
            if (filters.dateRange.end) {
                const endDate = new Date(filters.dateRange.end);
                endDate.setHours(23, 59, 59, 999); // Inclure toute la journée
                result = result.filter(group =>
                    new Date(group.created_at) <= endDate
                );
            }
        }

        // Filtre par durée
        if (filters?.duration) {
            if (filters.duration.min) {
                result = result.filter(group =>
                    group.max_duration >= Number(filters.duration.min)
                );
            }
            if (filters.duration.max) {
                result = result.filter(group =>
                    group.max_duration <= Number(filters.duration.max)
                );
            }
        }

        setFilteredGroups(result);
    }, [filters, groups]);

    const handleDeleteGroup = async (groupId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe de projets ?')) {
            try {
                await projectService.deleteProject(groupId);
                setGroups(prevGroups => prevGroups.filter(g => g.group_id !== groupId));
                setFilteredGroups(prevGroups => prevGroups.filter(g => g.group_id !== groupId));
            } catch (err) {
                console.error('Erreur lors de la suppression:', err);
            }
        }
    };

    const handleEdit = (project) => {
        setSelectedProject(project);
        setModalOpen(true);
    };

    const handleLanguageSelect = (projectId) => {
        navigate(`/main/${projectId}`);
        setModalOpen(false);
    };

    if (loading) return <div className="loading">Chargement des projets...</div>;
    if (error) return <div className="error">{error}</div>;
    if (filteredGroups.length === 0) {
        return (
            <div className="no-results">
                <i className="fas fa-search"></i>
                <p>Aucun projet ne correspond à vos critères de recherche</p>
            </div>
        );
    }

    return (
        <>
            <div className="projects-grid">
                {filteredGroups.map(group => (
                    <ProjectCard
                        key={group.group_id}
                        group={group}
                        onDelete={() => handleDeleteGroup(group.group_id)}
                        onEdit={() => handleEdit(group)}
                        onDownload={() => handleDownload(group)}
                    />
                ))}
            </div>

            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                project={selectedProject}
                onLanguageSelect={handleLanguageSelect}
            />
            <DownloadModal
                isOpen={downloadModalOpen}
                onClose={() => setDownloadModalOpen(false)}
                project={selectedDownloadProject}
            />
        </>
    );
};

export default ProjectGrid;